import {
  get,
  post,
  put,
  getDrawProgressData,
  getBudgetReconcileData
} from "../../network/apiService";
import { getBudgetData } from "../budgetReview/budgetReview.action";
import { apiUrlHost } from "../servicerOnboarding/servicerOnboarding.action";
export const getLoanInfoForPL = (loanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/aggregate/loanInfo?toorakLoanId=${loanId}`;
  return get(url);
};

export const updatePurchaseFlags = (requestBody: any) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/flag`;
  return put(url, requestBody);
};

export const updateDrawDocsTags = (documentId: string, requestBody: any[]) => {
  const url = `${apiUrlHost}/drawservice/assetmanagement/document/${documentId}/tags`;
  return put(url, requestBody);
};

export const updateDrawDocsAssets = (requestBody: any[], documentId: any) => {
  let url = `${apiUrlHost}/drawservice/assetmanagement/document/assets`;
  if (!requestBody || requestBody.length === 0) {
    url += `?documentId=${documentId}`;
  }
  return put(url, requestBody);
};

export const updateDocsReviewStatus = (requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/updateStatus`;
  return post(url, requestBody);
};

export const transferDocs = (documentId: string, requestBody: any) => {
  const url = `${apiUrlHost}/documentdetails/${documentId}/transfer`;
  return put(url, requestBody);
};

export const importDocs = (toorakLoanId: string, requestBody: any) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/import`;
  return put(url, requestBody);
};

export const fetchDrawProgress = (toorakLoanId: string, requiredDraw: any) => {
  return async (dispatch: any) => {
    try {
      // Step 1: Call getBudgetData through Redux dispatch to get line items
      let budgetDataResponse: any = null;

      // Create a wrapper promise that resolves when getBudgetData callback is called
      const budgetDataPromise = new Promise<void>((resolve) => {
        const thunkAction = getBudgetData(
          toorakLoanId,
          0,
          (_selectedDocId: any, _budgetLines: any, response: any) => {
            budgetDataResponse = response;
            resolve();
          }
        );
        dispatch(thunkAction);
      });

      await budgetDataPromise;

      // Step 2: Check if budget approval status is "Approved"
      const budgetApprovalStatus = budgetDataResponse?.budgetApprovalStatus;
      if (budgetApprovalStatus !== "Approved") {
        // Only return empty if budget is NOT approved
        return { lineItems: [], draws: [], approved: false };
      }

      // Extract line items from budget data (if available)
      const budgetLineItems =
        budgetDataResponse?.budgetDocumentInfoList?.[0]?.budgetLineItemList || [];

      // Step 3: Extract line items from budget data
      const lineItemsMap = new Map<number, any>();
      budgetLineItems.forEach((item: any) => {
        lineItemsMap.set(item.budgetLineItemId, {
          item: item.item || "",
          description: item.description || "",
          desc: item.description || "",
          budget: item.budget || "",
          financedBudget: item.financedBudget || "",
          inspectionInfo: {}
        });
      });

      // If no line items, return empty line items but with approved flag
      if (budgetLineItems.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Step 4: Get all draws and filter out unsuccessful stages
      const allDraws =
        requiredDraw?.allDraws?.filter(
          (draw: any) =>
            draw?.drawNumber !== undefined &&
            draw?.drawNumber !== null &&
            draw?.stage !== "Decline" &&
            draw?.stage !== "Draft" &&
            draw?.stage !== "Hold"
        ) || [];

      if (allDraws.length === 0) {
        const lineItems = Array.from(lineItemsMap.values());
        return { lineItems, draws: [], approved: true };
      }

      // Sort draws by drawNumber
      allDraws.sort(
        (a: any, b: any) => Number(a.drawNumber) - Number(b.drawNumber)
      );

      const qualifyingDraws = allDraws.filter(
        (draw: any) =>
          draw?.budgetReconcileStatus === "Completed" &&
          (draw?.inspectionReviewStatus === "Approved" ||
            draw?.inspectionReviewStatus === "Approved Conditionally")
      );

      // Call budget reconcile endpoint only for qualifying draws
      const drawDataPromises = qualifyingDraws.map((draw: any) =>
        getBudgetReconcileData(draw.drawId).catch((err: any) => {
          console.error(
            `Error fetching budget reconcile for draw ${draw.drawId}:`,
            err
          );
          return { data: [] };
        })
      );

      const qualifyingDrawDataResponses = await Promise.all(drawDataPromises);

      // Create a map of draw data for quick lookup
      const drawDataMap = new Map<string, any>();
      qualifyingDraws.forEach((draw: any, idx: number) => {
        const drawId = draw.drawId.toString();
        drawDataMap.set(drawId, qualifyingDrawDataResponses[idx]?.data || []);
      });

      const processedDraws: any[] = [];

      allDraws.forEach((draw: any) => {
        const drawId = draw.drawId.toString();
        const isQualifying = qualifyingDraws.some(
          (d: any) => d.drawId === draw.drawId
        );
        const reconciledItems = isQualifying
          ? drawDataMap.get(drawId) || []
          : [];
        const hasData = reconciledItems.length > 0;

        processedDraws.push({
          drawId,
          lineItems: Array.from(lineItemsMap.values()).map((lineItem: any) => {
            // Match by item name
            const matchingItem = hasData
              ? reconciledItems.find(
                  (item: any) =>
                    item.budgetLineItem.item.trim() === lineItem.item.trim()
                )
              : null;

            if (!isQualifying || !hasData || !matchingItem) {
              // For non-qualifying draws or draws without reconcile data, show "-|-"
              return {
                item: lineItem.item,
                desc: lineItem.desc,
                amount: "-",
                completion: "-"
              };
            }

            // For qualifying draws with data, calculate the approved draw amount
            const financedBudget =
              Number(matchingItem.budgetLineItem?.financedBudget) || 0;
            const approvedLatestCompletion =
              Number(matchingItem.approvedLatestCompletion) || 0;
            const previousCompletion =
              Number(matchingItem.previousCompletion) || 0;

            // Calculate approved draw amount using the formula:
            const approvedDrawAmount = (
              (financedBudget *
                (approvedLatestCompletion - previousCompletion)) /
              100
            ).toFixed(2);

            return {
              item: lineItem.item,
              desc: lineItem.desc,
              amount: approvedDrawAmount,
              completion: approvedLatestCompletion.toFixed(2)
            };
          })
        });

        // Update inspection info for line items
        reconciledItems.forEach((item: any) => {
          const lineItemId = item.budgetLineItemsId;
          if (lineItemsMap.has(lineItemId)) {
            const lineItem = lineItemsMap.get(lineItemId)!;
            lineItem.inspectionInfo[drawId] = {
              latestCompletion:
                isQualifying && hasData ? item.approvedLatestCompletion : "-",
              latestInspectionBudget: item.budgetLineItem?.financedBudget,
              previousCompletion: item.previousCompletion
            };
          }
        });
      });

      const lineItems = Array.from(lineItemsMap.values());
      return { lineItems, draws: processedDraws, approved: true };
    } catch (err) {
      console.error(err);
      return { lineItems: [], draws: [], approved: false };
    }
  };
};

export const startTaggingDocs = (toorakLoanId: string) => {
  const url = `${apiUrlHost}/purchasedloans/loan/${toorakLoanId}/tag`;
  return put(url);
};
